import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Target, Handshake, Lightbulb, ArrowRight } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { Button } from '@/components/ui/Button'
import { scrollToSection } from '@/lib/utils'

const values = [
  { id: 'quality', icon: Target },
  { id: 'communication', icon: Handshake },
  { id: 'innovation', icon: Lightbulb },
]

export function About() {
  const { t } = useTranslation()
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-32 bg-background-secondary"
      aria-labelledby="about-heading"
    >
      <div className="container mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-16">
            <span className="text-primary font-medium text-base sm:text-lg">{t('about.subtitle')}</span>
            <h2 id="about-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-4 sm:mb-6">
              {t('about.title')}
            </h2>
          </motion.div>

          {/* Personal Introduction */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          >
            {/* Profile Image Placeholder */}
            <div className="flex justify-center lg:justify-end order-1 lg:order-1">
              <motion.div
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative elements */}
                <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-full h-full bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-20" />
                <div className="absolute -bottom-3 -right-3 sm:-bottom-4 sm:-right-4 w-full h-full border-2 border-primary/30 rounded-2xl" />

                {/* Profile Image */}
                <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden">
                  <img
                    src="/profile.jpg"
                    alt={t('about.photoAlt')}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            </div>

            {/* About Text */}
            <div className="order-2 lg:order-2">
              <p className="text-foreground-secondary text-base sm:text-lg leading-relaxed mb-4 sm:mb-6">
                {t('about.description')}
              </p>
              <p className="text-foreground-secondary text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                {t('about.personalNote')}
              </p>

              {/* Values */}
              <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {values.map((value, index) => (
                  <motion.div
                    key={value.id}
                    className="flex items-start gap-3 sm:gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    <div className="p-2 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                      <value.icon className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-semibold text-foreground text-sm sm:text-base">{t(`about.values.${value.id}.title`)}</h4>
                      <p className="text-foreground-secondary text-xs sm:text-sm leading-relaxed">{t(`about.values.${value.id}.description`)}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <Button
                variant="primary"
                onClick={() => scrollToSection('contact')}
                className="group w-full sm:w-auto"
              >
                {t('about.cta')}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
